import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { Dialog, Popover } from '@mui/material'
import { CloseRounded, DeleteSweepOutlined, DoneAllRounded, NotificationsNoneRounded } from '@mui/icons-material'
import { useDispatch, useSelector } from '../../store'
import { addAuthLocalStorageItemToArray, getAuthLocalStorageArray } from '../../function/storage'
import { LocalStorage } from '../../enum/storage'
import { clearNotifications, fetchNotification, readNotification } from '../../store/support/actionCreator'
import { Notification as INotification } from '../../store/support/reducer'
import { getRefreshedToken } from '../../store/auth/selector'
import { useAdminTheme } from '../../hook/useAdminTheme'
import { clearSupportCounts, useSupportAlerts } from '../../hook/useSupportAlerts'
import { useNavigate } from 'react-router-dom'
import l from '../../page/SubscribersPageV2.module.css'
import h from './HeaderV2.module.css'

/*
 * The admin's notifications in the v2 header — the same data and actions as
 * the classic Notification (which the classic pages keep): the list from the
 * store, read ones remembered in localStorage, more loaded at the list's end,
 * refreshed every few minutes, "Clear all". A notification opens in a dialog.
 */

const LIMIT = 30
const REFRESH_MINUTES = 3

export function NotificationV2() {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { theme } = useAdminTheme()
  const refreshedToken = useSelector(getRefreshedToken)
  const { notifications } = useSelector((state) => state.support)

  const [anchor, setAnchor] = React.useState<HTMLElement | null>(null)
  const [offset, setOffset] = React.useState(0)
  const [open, setOpen] = React.useState<INotification | null>(null)
  // read ones live in localStorage; this re-reads them after a change
  const [readTick, setReadTick] = React.useState(0)
  // "Clear all" deletes them for good — asked once more
  const [clearAsk, setClearAsk] = React.useState(false)
  const navigate = useNavigate()

  // a support alert just came — it is in the list at once, not after the next refresh
  useSupportAlerts(() => dispatch(fetchNotification(0, LIMIT, undefined, true)))

  React.useEffect(() => {
    if (refreshedToken === null) return
    dispatch(fetchNotification(offset, LIMIT, offset > 0))
  }, [refreshedToken, dispatch, offset])

  React.useEffect(() => {
    if (refreshedToken === null) return undefined
    const id = window.setInterval(() => dispatch(fetchNotification(0, LIMIT, undefined, true)), REFRESH_MINUTES * 60 * 1000)
    return () => window.clearInterval(id)
  }, [refreshedToken, dispatch])

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const read = React.useMemo(() => new Set(getAuthLocalStorageArray(LocalStorage.RED_NOTIFICATION)), [readTick, notifications])
  const unread = notifications.filter((n) => !read.has(n.id)).length
  // the last page came back full — there may be more
  const hasMore = notifications.length >= offset + LIMIT

  function markRead(n: INotification) {
    if (read.has(n.id)) return
    addAuthLocalStorageItemToArray(LocalStorage.RED_NOTIFICATION, n.id)
    dispatch(readNotification(n.id))
    setReadTick((x) => x + 1)
  }

  function openOne(n: INotification) {
    markRead(n)
    setAnchor(null)
    // a support alert opens its ticket
    if (n.ticketId) navigate(`/support_v2?ticket=${n.ticketId}`)
    else setOpen(n)
  }

  function markAll() {
    notifications.forEach((n) => {
      if (read.has(n.id)) return
      addAuthLocalStorageItemToArray(LocalStorage.RED_NOTIFICATION, n.id)
      dispatch(readNotification(n.id))
    })
    setReadTick((x) => x + 1)
  }

  function onScroll(e: React.UIEvent<HTMLDivElement>) {
    const el = e.currentTarget
    if (hasMore && el.scrollTop + el.clientHeight >= el.scrollHeight - 40) setOffset(notifications.length)
  }

  return (
    <>
      <button
        type="button"
        className={`${h.btn} ${anchor ? h.btnOn : ''}`}
        onClick={(e) => setAnchor(anchor ? null : e.currentTarget)}
        aria-label={unread ? `${unread} unread notifications` : 'Notifications'}
        title="Notifications"
      >
        <NotificationsNoneRounded sx={{ fontSize: 19 }} />
        {unread > 0 && <span className={h.badge}>{unread > 99 ? '99+' : unread}</span>}
      </button>

      <Popover
        open={!!anchor}
        anchorEl={anchor}
        onClose={() => { setAnchor(null); setClearAsk(false) }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ sx: { background: 'transparent', boxShadow: 'none', overflow: 'visible' } }}
      >
        <div className={`${l.theme} ${h.panel}`} data-theme={theme}>
          <div className={h.panelHead}>
            <b>Notifications</b>
            {unread > 0 && <span className={h.count}>{unread} new</span>}
            {unread > 0 && <button type="button" className={h.link} onClick={markAll}><DoneAllRounded sx={{ fontSize: 15 }} />Mark all read</button>}
          </div>
          {notifications.length ? (
            <div className={h.list} onScroll={onScroll}>
              {notifications.map((n) => (
                <button key={n.id} type="button" className={`${h.item} ${read.has(n.id) ? '' : h.unread}`} onClick={() => openOne(n)}>
                  <i className={h.dot} />
                  <span className={h.itemText}>
                    <b>{n.title}</b>
                    {n.text && <span>{n.text}</span>}
                  </span>
                </button>
              ))}
              {hasMore && <div className={h.more}>Loading more…</div>}
            </div>
          ) : (
            <div className={h.empty}>{t('notification.hasNo')}</div>
          )}
          {notifications.length > 0 && (
            clearAsk ? (
              <div className={`${h.panelFoot} ${h.ask}`}>
                <span>Delete all {notifications.length}{hasMore ? '+' : ''} notifications? This can’t be undone.</span>
                <button type="button" className={h.link} onClick={() => setClearAsk(false)}>Cancel</button>
                <button
                  type="button"
                  className={`${h.link} ${h.danger}`}
                  onClick={() => { dispatch(clearNotifications()); clearSupportCounts(); setClearAsk(false); setOffset(0) }}
                >
                  Delete
                </button>
              </div>
            ) : (
              <div className={h.panelFoot}>
                <button type="button" className={`${h.link} ${h.danger}`} onClick={() => setClearAsk(true)}><DeleteSweepOutlined sx={{ fontSize: 16 }} />Clear all</button>
              </div>
            )
          )}
        </div>
      </Popover>

      <Dialog open={!!open} onClose={() => setOpen(null)} maxWidth={false} PaperProps={{ sx: { background: 'transparent', boxShadow: 'none', m: 2 } }}>
        <div className={`${l.theme} ${h.dialog}`} data-theme={theme}>
          <div className={h.dialogHead}>
            <b>{open?.title}</b>
            <button type="button" className={h.close} onClick={() => setOpen(null)} aria-label={t('common.close')}><CloseRounded sx={{ fontSize: 18 }} /></button>
          </div>
          <div className={h.dialogBody}>{open?.text}</div>
        </div>
      </Dialog>
    </>
  )
}
